import type { DowngradeChoices, DowngradePreview, Locale } from '@icetus/contracts';

/**
 * 降档页的"预选"与"还差什么"。
 *
 * 抽成纯模块（不 import React）有三个理由：
 *   1) 预选规则决定了客户点开弹窗时看到的是什么，它必须是可断言的 ——
 *      "默认帮我留了哪个站"如果只能靠点一遍看，那它会在某次改动后悄悄变；
 *   2) 服务端的校验文案是给接口调用者的，页面需要同一套判断但更早地
 *      把"确认"按钮灰掉，否则客户要点了才知道没选够；
 *   3) 两边规则必须同源。这里写的每条都与 `apps/platform/src/billing/downgrade.ts`
 *      一一对应，改一处就要改另一处 —— 差异的后果是"页面允许提交、服务端拒绝"。
 */

/** 该保留几个。与服务端 `keepLimit` 同一口径：配额 0 表示不限量 */
export function keepLimit(quota: number, total: number): number {
  if (quota <= 0) return total;
  return Math.min(quota, total);
}

/**
 * 打开弹窗时的默认选择。
 *
 * 三条取舍：
 *   · **站点留最早的几个**（`items` 已按 `created_at` 排）。第一个站是注册那天
 *     自动建的那个，客户所有对外宣传的地址都在它上面 —— 默认停它最容易被骂。
 *     这也是老代码里"留最早建的"那条兜底规则，延续下来客户不会觉得行为变了。
 *   · **语言留默认语言 + 页面最多的**。默认语言不能关；其余的按"关掉损失最小"
 *     排，客户大概率直接确认。
 *   · **成员先留 owner，再留最早加入的**。owner 不能移除，最早加入的通常是
 *     实际在管事的人。
 */
export function defaultChoices(preview: DowngradePreview): DowngradeChoices {
  const siteKeep = keepLimit(preview.sites.limit, preview.sites.items.length);
  const sites = preview.sites.items.slice(0, siteKeep).map((s) => s.id);

  const locales: Record<string, Locale[]> = {};
  for (const group of preview.locales.groups) {
    const limit = keepLimit(group.limit, group.items.length);
    if (group.items.length <= limit) {
      locales[group.siteId] = group.items.map((i) => i.locale);
      continue;
    }
    const sorted = [...group.items].sort((a, b) => {
      // 默认语言永远排第一；其余按页面数从多到少（页面少的关掉损失最小）
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return b.pages - a.pages;
    });
    locales[group.siteId] = sorted.slice(0, limit).map((i) => i.locale);
  }

  const memberKeep = keepLimit(preview.members.limit, preview.members.items.length);
  const ordered = [...preview.members.items].sort((a, b) => {
    const aOwner = a.role === 'owner' ? 0 : 1;
    const bOwner = b.role === 'owner' ? 0 : 1;
    if (aOwner !== bOwner) return aOwner - bOwner;
    // 然后是"自己"：把自己预选成要移除，客户会以为系统踢他走
    const aSelf = a.isSelf ? 0 : 1;
    const bSelf = b.isSelf ? 0 : 1;
    return aSelf - bSelf;
  });
  const keep = new Set(ordered.slice(0, memberKeep).map((m) => m.userId));
  const members = preview.members.items.filter((m) => !keep.has(m.userId)).map((m) => m.userId);

  return { sites, locales, members };
}

/**
 * 还差什么才能提交。空数组 = 可以提交。
 *
 * 返回的是**给客户看的一句话**，不是错误码：页面把它显示在确认按钮上方，
 * 客户改一个勾它就少一条。
 */
export function choiceProblems(preview: DowngradePreview, choices: DowngradeChoices): string[] {
  const problems: string[] = [];

  const siteMax = keepLimit(preview.sites.limit, preview.sites.items.length);
  if (choices.sites.length === 0) {
    problems.push('至少要保留一个站点');
  } else if (choices.sites.length > siteMax) {
    problems.push(`目标套餐最多 ${preview.to.quotas.sites} 个站点，最多只能保留 ${siteMax} 个`);
  }

  for (const group of preview.locales.groups) {
    const picked = choices.locales[group.siteId];
    // 没提交 = 这一站的语言不动，只有"本来就没超"时才允许
    if (picked === undefined) {
      if (group.limit > 0 && group.items.length > group.limit) {
        problems.push(`「${group.siteName}」要选保留哪几种语言`);
      }
      continue;
    }
    const isDefault = group.items.find((i) => i.isDefault);
    if (isDefault && !picked.includes(isDefault.locale)) {
      problems.push(`「${group.siteName}」的默认语言不能关闭`);
    }
    if (group.limit > 0 && picked.length > group.limit) {
      problems.push(`「${group.siteName}」最多保留 ${group.limit} 种语言`);
    }
  }

  const memberMax = keepLimit(preview.members.limit, preview.members.items.length);
  const remaining = preview.members.items.length - choices.members.length;
  if (preview.members.limit > 0 && remaining > memberMax) {
    problems.push(`还要移除 ${remaining - memberMax} 位成员（目标套餐 ${preview.members.limit} 个席位）`);
  }
  if (remaining < 1) {
    problems.push('至少要保留一个成员');
  }

  return problems;
}

/** 勾选/取消一个站点，返回新的选择（不改原对象） */
export function toggleSite(choices: DowngradeChoices, siteId: string): DowngradeChoices {
  const has = choices.sites.includes(siteId);
  return {
    ...choices,
    sites: has ? choices.sites.filter((id) => id !== siteId) : [...choices.sites, siteId],
  };
}

/** 勾选/取消一位成员（语义是"要移除"）。owner 与本人由调用方禁掉 */
export function toggleMember(choices: DowngradeChoices, userId: string): DowngradeChoices {
  const has = choices.members.includes(userId);
  return {
    ...choices,
    members: has ? choices.members.filter((id) => id !== userId) : [...choices.members, userId],
  };
}

/** 勾选/取消某站点的一种语言 */
export function toggleLocale(
  choices: DowngradeChoices,
  siteId: string,
  locale: Locale,
): DowngradeChoices {
  const current = choices.locales[siteId] ?? [];
  const next = current.includes(locale)
    ? current.filter((l) => l !== locale)
    : [...current, locale];
  return { ...choices, locales: { ...choices.locales, [siteId]: next } };
}
